import { useEffect, useState, useMemo } from 'react'
import api from '../../services/api'
import {
  Shield, Plus, Trash2, Edit3, ChevronDown, ChevronRight,
  Layers, Check, X, AlertTriangle, Wand2, Users, Copy
} from 'lucide-react'

const INDUSTRIES = [
  { value: 'field_services', label: 'Field Services' },
  { value: 'construction', label: 'Construction' },
  { value: 'mining', label: 'Mining' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'healthcare', label: 'Healthcare' },
]

const LEVEL_COLORS: Record<number, string> = {
  1: '#EF4444', 2: '#F59E0B', 3: '#8B5CF6', 4: '#3B82F6', 5: '#10B981', 6: '#6B7280',
}
const LEVEL_LABELS: Record<number, string> = {
  1: 'Owner', 2: 'Admin', 3: 'Manager', 4: 'Supervisor', 5: 'Worker', 6: 'Viewer',
}

type Role = {
  id: string; name: string; slug: string; description?: string
  base_role: string; level: number; permissions: string[]; is_system: number; is_active: number
  department?: string; created_by_name?: string
}

type PermissionCatalogue = Record<string, { label: string; group: string }>

const BASE_ROLES = ['admin','manager','supervisor','worker','viewer']

export default function RolesPage() {
  const [roles, setRoles] = useState<Role[]>([])
  const [perms, setPerms] = useState<PermissionCatalogue>({})
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Role | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [templateIndustry, setTemplateIndustry] = useState('')
  const [applyingTemplate, setApplyingTemplate] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  const [form, setForm] = useState({
    name: '', slug: '', description: '', base_role: 'worker',
    level: 5, department: '', permissions: [] as string[],
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    Promise.all([
      api.get('/roles'),
      api.get('/roles/permissions'),
    ]).then(([rolesRes, permsRes]) => {
      setRoles(rolesRes.data.data || [])
      setPerms(permsRes.data.permissions || {})
      // expand all groups
      const groups = new Set(Object.values(permsRes.data.permissions || {}).map((p: any) => p.group))
      setExpandedGroups(groups as Set<string>)
    }).finally(() => setLoading(false))
  }, [])

  const permsByGroup = useMemo(() => {
    const groups: Record<string, { key: string; label: string }[]> = {}
    for (const [key, val] of Object.entries(perms)) {
      if (!groups[val.group]) groups[val.group] = []
      groups[val.group].push({ key, label: val.label })
    }
    return groups
  }, [perms])

  const hierarchy = useMemo(() => {
    const byLevel: Record<number, Role[]> = {}
    for (const r of roles) {
      if (!byLevel[r.level]) byLevel[r.level] = []
      byLevel[r.level].push(r)
    }
    return byLevel
  }, [roles])

  function openCreate() {
    setForm({ name: '', slug: '', description: '', base_role: 'worker', level: 5, department: '', permissions: [] })
    setSelected(null)
    setShowCreate(true)
    setError('')
  }

  function openEdit(role: Role) {
    setForm({
      name: role.name, slug: role.slug, description: role.description || '',
      base_role: role.base_role, level: role.level, department: role.department || '',
      permissions: [...role.permissions],
    })
    setSelected(role)
    setShowCreate(true)
    setError('')
  }

  async function handleSave() {
    if (!form.name.trim()) { setError('Name is required'); return }
    setSaving(true); setError('')
    try {
      if (selected && !selected.id.startsWith('builtin:')) {
        const r = await api.patch(`/roles/${selected.id}`, form)
        setRoles(roles.map(ro => ro.id === selected.id ? { ...ro, ...r.data } : ro))
      } else {
        const r = await api.post('/roles', form)
        setRoles([...roles, r.data])
      }
      setShowCreate(false)
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to save')
    } finally { setSaving(false) }
  }

  async function handleDelete(role: Role) {
    if (!confirm(`Delete role "${role.name}"?`)) return
    try {
      await api.delete(`/roles/${role.id}`)
      setRoles(roles.filter(r => r.id !== role.id))
    } catch (e: any) {
      alert(e.response?.data?.error || 'Cannot delete')
    }
  }

  async function applyTemplate() {
    if (!templateIndustry) return
    setApplyingTemplate(true)
    try {
      const r = await api.post('/roles/apply-template', { industry: templateIndustry })
      alert(`Created ${r.data.count} roles from ${templateIndustry} template`)
      const rolesRes = await api.get('/roles')
      setRoles(rolesRes.data.data || [])
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed')
    } finally { setApplyingTemplate(false) }
  }

  function togglePerm(key: string) {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter(p => p !== key)
        : [...f.permissions, key],
    }))
  }

  function toggleGroup(group: string) {
    const groupKeys = (permsByGroup[group] || []).map(p => p.key)
    const allOn = groupKeys.every(k => form.permissions.includes(k))
    setForm(f => ({
      ...f,
      permissions: allOn
        ? f.permissions.filter(k => !groupKeys.includes(k))
        : [...new Set([...f.permissions, ...groupKeys])],
    }))
  }

  function applyBaseRole(baseRole: string) {
    const { ROLE_DEFAULTS } = { ROLE_DEFAULTS: {} as Record<string, string[]> }
    // Fetch base role permissions from server-side defaults
    const baseRolePerms: Record<string, string[]> = {
      admin: Object.keys(perms),
      manager: Object.keys(perms).filter(k => !k.startsWith('settings:billing') && !k.startsWith('settings:admin')),
      supervisor: Object.keys(perms).filter(k => ['incidents','investigations','actions','permits','audits','workforce','competencies','assets','loneworker','psychosocial'].some(g => k.startsWith(g + ':read') || k.startsWith(g + ':create') || k.startsWith(g + ':conduct') || k.startsWith(g + ':use'))),
      worker: Object.keys(perms).filter(k => k.endsWith(':read') || k.endsWith(':create') || k === 'loneworker:use' || k === 'audits:conduct'),
      viewer: Object.keys(perms).filter(k => k.endsWith(':read') || k === 'reports:view'),
    }
    setForm(f => ({ ...f, base_role: baseRole, permissions: baseRolePerms[baseRole] || [] }))
  }

  if (loading) return <div className="p-8 text-gray-500">Loading roles...</div>

  return (
    <div className="p-6 max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield size={24} className="text-blue-600" /> Role Hierarchy
          </h1>
          <p className="text-gray-500 text-sm mt-1">Define roles, permissions, and access levels for your organisation</p>
        </div>
        <div className="flex gap-2">
          <div className="flex gap-2 items-center">
            <select
              value={templateIndustry}
              onChange={e => setTemplateIndustry(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Apply industry template...</option>
              {INDUSTRIES.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
            </select>
            <button
              onClick={applyTemplate}
              disabled={!templateIndustry || applyingTemplate}
              className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-purple-700"
            >
              <Wand2 size={14} />
              {applyingTemplate ? 'Applying…' : 'Apply'}
            </button>
          </div>
          <button onClick={openCreate} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
            <Plus size={16} /> New Role
          </button>
        </div>
      </div>

      {/* Hierarchy Visual */}
      <div className="mb-6 bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <Layers size={16} className="text-blue-500" /> Organisation Hierarchy
        </h2>
        <div className="flex flex-col gap-2">
          {[1,2,3,4,5,6].map(level => {
            const levelRoles = hierarchy[level] || []
            if (levelRoles.length === 0) return null
            return (
              <div key={level} className="flex items-start gap-4">
                <div className="flex items-center gap-2 w-28 shrink-0">
                  <div className="w-3 h-3 rounded-full" style={{ background: LEVEL_COLORS[level] }} />
                  <span className="text-xs font-medium text-gray-500">{LEVEL_LABELS[level]}</span>
                </div>
                <div className="flex flex-wrap gap-2 flex-1">
                  {levelRoles.map(role => (
                    <button
                      key={role.id}
                      onClick={() => !role.id.startsWith('builtin:') && openEdit(role)}
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all hover:shadow-sm"
                      style={{
                        background: `${LEVEL_COLORS[level]}15`,
                        borderColor: `${LEVEL_COLORS[level]}50`,
                        color: LEVEL_COLORS[level],
                      }}
                    >
                      {role.is_system ? <Shield size={10} /> : <Users size={10} />}
                      {role.name}
                      {role.is_system ? '' : ' ✎'}
                    </button>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Role Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map(role => (
          <div key={role.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: LEVEL_COLORS[role.level] }} />
                <span className="font-semibold text-gray-900 text-sm">{role.name}</span>
                {role.is_system ? (
                  <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">system</span>
                ) : (
                  <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">custom</span>
                )}
              </div>
              {!role.is_system && (
                <div className="flex gap-1">
                  <button onClick={() => openEdit(role)} className="p-1 text-gray-400 hover:text-blue-600 rounded">
                    <Edit3 size={13} />
                  </button>
                  <button onClick={() => handleDelete(role)} className="p-1 text-gray-400 hover:text-red-600 rounded">
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </div>
            {role.description && <p className="text-xs text-gray-500 mb-2">{role.description}</p>}
            <div className="flex items-center justify-between text-xs text-gray-400 mt-2">
              <span>{LEVEL_LABELS[role.level]} · {role.base_role}</span>
              <span>{role.permissions.length} permissions</span>
            </div>
            {!role.is_active && (
              <div className="mt-2 text-xs text-red-500 flex items-center gap-1">
                <AlertTriangle size={11} /> Inactive
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create/Edit Drawer */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end">
          <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="font-semibold text-gray-900 text-lg">
                {selected && !selected.id.startsWith('builtin:') ? `Edit: ${selected.name}` : 'New Role'}
              </h2>
              <button onClick={() => setShowCreate(false)} className="p-2 text-gray-400 hover:text-gray-700 rounded">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 p-5 space-y-4 overflow-y-auto">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Role Name *</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="e.g. Site Safety Officer" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Slug</label>
                  <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="site_safety_officer" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Base Role</label>
                  <select value={form.base_role} onChange={e => applyBaseRole(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    {BASE_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Level</label>
                  <select value={form.level} onChange={e => setForm(f => ({ ...f, level: Number(e.target.value) }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    {[1,2,3,4,5,6].map(l => <option key={l} value={l}>{l} — {LEVEL_LABELS[l]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Department</label>
                  <input value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Optional" />
                </div>
              </div>

              {/* Permissions Matrix */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Permissions</label>
                  <span className="text-xs text-gray-500">{form.permissions.length} selected</span>
                </div>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  {Object.entries(permsByGroup).map(([group, groupPerms]) => {
                    const allOn = groupPerms.every(p => form.permissions.includes(p.key))
                    const someOn = groupPerms.some(p => form.permissions.includes(p.key))
                    const open = expandedGroups.has(group)
                    return (
                      <div key={group} className="border-b border-gray-100 last:border-0">
                        <button
                          onClick={() => setExpandedGroups(eg => {
                            const n = new Set(eg)
                            n.has(group) ? n.delete(group) : n.add(group)
                            return n
                          })}
                          className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-left"
                        >
                          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          <span className="flex-1 text-xs font-semibold text-gray-700">{group}</span>
                          <button
                            onClick={e => { e.stopPropagation(); toggleGroup(group) }}
                            className={`text-xs px-2 py-0.5 rounded ${allOn ? 'bg-blue-600 text-white' : someOn ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'}`}
                          >
                            {allOn ? 'All on' : someOn ? 'Partial' : 'All off'}
                          </button>
                        </button>
                        {open && (
                          <div className="px-4 py-2 grid grid-cols-2 gap-1">
                            {groupPerms.map(p => (
                              <label key={p.key} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer py-1">
                                <input type="checkbox" checked={form.permissions.includes(p.key)}
                                  onChange={() => togglePerm(p.key)}
                                  className="rounded border-gray-300 text-blue-600" />
                                {p.label}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 p-4 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setShowCreate(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving…' : selected && !selected.id.startsWith('builtin:') ? 'Update Role' : 'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
